import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useVisitor } from '../context/VisitorContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiGlobe, FiTrash2, FiCopy, FiCheck, FiAlertTriangle, FiRefreshCw } = FiIcons;

const Projects = () => {
  const { projects, addProject, removeProject, user } = useVisitor();
  const [showAddForm, setShowAddForm] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({ name: '', url: '', description: '' });

  const APP_URL = "https://shiny-puffpuff-add900.netlify.app";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.name && formData.url) {
      setIsCreating(true);
      const success = await addProject(formData);
      setIsCreating(false);
      if (success) {
        setFormData({ name: '', url: '', description: '' });
        setShowAddForm(false);
      }
    }
  };

  const copyCode = (id) => {
    const code = `<script src="${APP_URL}/tracker.js" async data-project-id="${id}"></script>`;
    navigator.clipboard.writeText(code);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Projects</h1>
          <p className="text-gray-600">Websites currently being tracked</p>
        </div>
        <button 
          onClick={() => setShowAddForm(true)} 
          disabled={!user}
          className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${user ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
        >
          <SafeIcon icon={FiPlus} />
          <span>New Project</span>
        </button>
      </div>

      {!user && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6 flex items-start">
          <SafeIcon icon={FiAlertTriangle} className="text-orange-500 mt-1 mr-3 text-lg" />
          <div>
            <h3 className="font-semibold text-orange-900">Authentication Required</h3>
            <p className="text-sm text-orange-800">You must be logged in to create and manage projects. Please go to the Dashboard to sign in.</p>
          </div>
        </div>
      )}

      {showAddForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} className="bg-white rounded-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Add Project</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Project Name</label>
                <input 
                  type="text" 
                  placeholder="My Awesome Site" 
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Website URL</label>
                <input 
                  type="url" 
                  placeholder="https://example.com" 
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  value={formData.url}
                  onChange={(e) => setFormData({...formData, url: e.target.value})}
                  required
                />
              </div>
              <div className="flex gap-2 mt-6">
                <button 
                  type="submit" 
                  disabled={isCreating}
                  className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 flex justify-center items-center"
                >
                  {isCreating ? <SafeIcon icon={FiRefreshCw} className="animate-spin" /> : 'Create Project'}
                </button>
                <button 
                  type="button" 
                  onClick={() => setShowAddForm(false)}
                  className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {projects.length > 0 ? (
          projects.map((project) => (
            <motion.div 
              key={project.id} 
              className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow"
            >
              <div className="flex justify-between items-start mb-4">
                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600">
                  <SafeIcon icon={FiGlobe} />
                </div>
                <button 
                  onClick={() => removeProject(project.id)}
                  className="text-gray-400 hover:text-red-500 p-1"
                >
                  <SafeIcon icon={FiTrash2} />
                </button>
              </div>
              <h3 className="font-bold text-gray-900 truncate">{project.name}</h3>
              <p className="text-sm text-gray-500 mb-4 truncate">{project.url}</p>
              <div className="pt-4 border-t border-gray-100 flex items-center justify-between">
                <span className="text-xs text-gray-400 font-mono" title={project.id}>ID: ...{project.id.slice(-8)}</span>
                <button 
                  onClick={() => copyCode(project.id)}
                  className={`text-sm flex items-center gap-1 px-2 py-1 rounded transition-colors ${copiedId === project.id ? 'bg-green-50 text-green-600 font-medium' : 'text-blue-600 hover:bg-blue-50'}`}
                >
                  <SafeIcon icon={copiedId === project.id ? FiCheck : FiCopy} />
                  {copiedId === project.id ? 'Copied' : 'Get Code'}
                </button>
              </div>
            </motion.div>
          ))
        ) : (
          <div className="col-span-full py-12 text-center bg-gray-50 border border-dashed border-gray-300 rounded-xl">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiGlobe} className="text-3xl text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900">No projects yet</h3>
            <p className="text-gray-500 mt-1 max-w-sm mx-auto">Create your first project to start tracking visitors on your website.</p>
            {user && (
              <button 
                onClick={() => setShowAddForm(true)}
                className="mt-4 text-blue-600 font-medium hover:underline"
              >
                Create a Project
              </button>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default Projects;